import React from "react";
import {
  Button as MuiButton,
  CircularProgress,
  IconButton as MuiIconButton,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import { FormikContext } from "formik";

const useStyles = makeStyles((theme) => ({
  root: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 6,
  },
  primary: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  outlined: {
    borderColor: theme.palette.divider,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(25, 118, 210, 0.04)",
    },
  },
  iconButton: {
    padding: theme.spacing(0.5),
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
  },
  smallIcon: {
    padding: 4,
    "& .MuiSvgIcon-root": {
      fontSize: 14,
    },
  },

  deleteButton: {
    backgroundColor: theme.palette.danger.dark,
    color: theme.palette.danger.contrastText,

    "&:hover": {
      backgroundColor: "#b71c1c",
    },
  },
  submitButton: {
    backgroundColor: "#ffd140",
    color: "#1f1f1f",

    "&:hover": {
      backgroundColor: "#ffd140",
    },
    "&.Mui-disabled": {
      backgroundColor: "#eaeaea !important",
      color: "#c0c0c0 !important",
      // opacity: 0.8,
    },
  },
}));

// ------------------ UI Component ------------------ //
export const SKButton = ({ variant = "text", color = "primary", className = "", ...props }) => {
  const classes = useStyles();

  const btnClasses = [
    classes.root,
    // color === "delete" && classes.deleteButton,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return <MuiButton className={btnClasses} variant={variant} color={color} {...props} />;
};

export const SKIconButton = ({ size = "medium", color = "default", className = "", tooltip, ...props }) => {
  const classes = useStyles();

  const iconButtonClasses = [
    classes.iconButton,
    size === "small" && classes.smallIcon,
    // color === "delete" && classes.deleteButton,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const button = <MuiIconButton className={iconButtonClasses} color={color} {...props} />;

  if (tooltip) {
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }

  return button;
};

// ------------------ Domain Service Layer ------------------ //
export const SKExportButton = ({
  variant = "text",
  color = "primary",
  className = "",
  onExport,
  label = "Export",
  loadingLabel = "Generating...",
  ...props
}) => {
  const classes = useStyles();
  const [isGenerating, setIsGenerating] = React.useState(false);

  const handleClick = async (e) => {
    if (!onExport) return;

    try {
      setIsGenerating(true);
      await onExport(e);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <SKButton
      className={`${classes.root} ${className}`}
      variant={variant}
      color={color}
      onClick={handleClick}
      disabled={isGenerating || props.disabled}
      {...props}
    >
      {isGenerating ? loadingLabel : label}
    </SKButton>
  );
};

export const SKSubmitButton = React.forwardRef(function SKSubmitButton(
  {
    variant = "contained",
    color = "primary",
    className = "",
    type = "submit",
    label,
    loadingLabel,
    children,
    disabled = false,
    loading,
    isLoading,
    showSpinner = true,
    spinnerPlacement = "end",
    disableOnInvalid = false,
    disableUnlessDirty = false,
    onClick,
    onSubmit,
    endIcon,
    startIcon,
    size = "medium",
    ...props
  },
  ref,
) {
  const classes = useStyles();
  const formik = React.useContext(FormikContext);
  const [isInternalSubmitting, setIsInternalSubmitting] = React.useState(false);

  const isSubmitting = Boolean(
    isLoading ?? loading ?? ((formik ? formik.isSubmitting : false) || isInternalSubmitting),
  );

  const isInvalid = Boolean(disableOnInvalid && formik && !formik.isValid);
  const isNotDirty = Boolean(disableUnlessDirty && formik && !formik.dirty);
  const isDisabled = Boolean(disabled || isSubmitting || isInvalid || isNotDirty);

  const handleClick = async (e) => {
    if (onClick) {
      await onClick(e);
      if (e?.defaultPrevented) return;
    }

    if (onSubmit) {
      try {
        setIsInternalSubmitting(true);
        await onSubmit(e);
      } finally {
        setIsInternalSubmitting(false);
      }
    }
  };

  const spinner = <CircularProgress color="inherit" size={size === "small" ? 16 : 20} />;
  const resolvedStartIcon = isSubmitting && showSpinner && spinnerPlacement === "start" ? spinner : startIcon;
  const resolvedEndIcon = isSubmitting && showSpinner && spinnerPlacement === "end" ? spinner : endIcon;
  const content = isSubmitting ? loadingLabel ?? label ?? children ?? "Submitting..." : label ?? children ?? "Submit";

  const btnClasses = [classes.root, classes.submitButton, className].filter(Boolean).join(" ");

  return (
    <SKButton
      ref={ref}
      className={btnClasses}
      type={type}
      variant={variant}
      color={color}
      size={size}
      disabled={isDisabled}
      onClick={handleClick}
      startIcon={resolvedStartIcon}
      endIcon={resolvedEndIcon}
      data-cy="submit"
      {...props}
    >
      {content}
    </SKButton>
  );
});
